import { ReactNode, useState } from 'react';
import { useAuditLog } from '@/hooks/useAuditLog';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  Target,
  FileText,
  LogOut,
  Menu,
  X,
  User,
  Snowflake,
  ShieldCheck,
  Settings,
  Calendar,
  HelpCircle,
  Lock,
  Sparkles,
  Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useSubscription } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';
import focoLogo from '@/assets/foco-ai-logo.png';
import { FinancialAdvisorChat } from '@/components/ai/FinancialAdvisorChat';
import { AccessBlockedScreen } from '@/components/layout/AccessBlockedScreen';
import { AppFooter } from '@/components/layout/AppFooter';
import { UserGuideTour } from '@/components/onboarding/UserGuideTour';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';
import { OnboardingReplayProvider } from '@/hooks/useOnboardingReplay';
import { PwaInstallNavButton } from '@/components/pwa/PwaInstallNavButton';

interface AppLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: 'Panel Principal', href: '/dashboard', icon: LayoutDashboard, premium: false },
  { name: 'Panel Anual', href: '/dashboard-anual', icon: Calendar, premium: true },
  { name: 'Transacciones', href: '/transactions', icon: Receipt, premium: false },
  { name: 'Metas', href: '/goals', icon: Target, premium: true },
  { name: 'Bola de Nieve', href: '/snowball', icon: Snowflake, premium: true },
  { name: 'Reportes', href: '/reports', icon: FileText, premium: false },
  { name: 'Configuración', href: '/settings', icon: Settings, premium: false },
];

const adminNavigation = [
  { name: 'Admin', href: '/admin', icon: ShieldCheck, premium: false },
];

export function AppLayout({ children }: AppLayoutProps) {
  const { user, signOut, isAdmin, accessStatus } = useAuth();
  const { profile } = useProfile();
  const { isPremium } = useSubscription();
  const allNavigation = isAdmin ? [...navigation, ...adminNavigation] : navigation;
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  useAuditLog();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const isAccessBlocked = !isAdmin && accessStatus && (
    !accessStatus.isActive || 
    accessStatus.isSuspended || 
    accessStatus.accessExpired
  );

  if (isAccessBlocked) {
    return <AccessBlockedScreen />;
  }

  return (
    <OnboardingReplayProvider>
    <div className="min-h-screen min-h-[100dvh] bg-background">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-primary px-4 h-14 flex items-center justify-between safe-area-inset">
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <img src={focoLogo} alt="FOCO AI System" className="h-9 w-auto rounded" />
        </Link>
        <div className="flex items-center gap-1">
          <PwaInstallNavButton
            className="h-10 w-10 text-primary-foreground hover:bg-white/10"
            iconClassName="h-5 w-5"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 touch-manipulation text-primary-foreground hover:bg-white/10"
            onClick={() => setGuideOpen(true)}
            aria-label="Abrir guía de usuario"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 touch-manipulation text-primary-foreground hover:bg-white/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 pt-14">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="relative bg-card border-b p-2 space-y-0.5 max-h-[calc(100dvh-3.5rem)] overflow-y-auto">
            {allNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors touch-manipulation text-sm',
                    isActive 
                      ? 'bg-accent/10 text-accent font-medium' 
                      : 'text-foreground hover:bg-muted active:bg-muted'
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                  <span className="flex-1">{item.name}</span>
                  {!isPremium && !isAdmin && item.premium && (
                    <Lock className="h-3.5 w-3.5 opacity-50" />
                  )}
                </Link>
              );
            })}
            {!isPremium && !isAdmin && (
              <Link
                to="/upgrade"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary/10 text-primary font-medium touch-manipulation text-sm"
              >
                <Sparkles className="h-[18px] w-[18px]" />
                Actualizar a Premium
              </Link>
            )}
            {isPremium && !isAdmin && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-400/10 text-yellow-400 text-sm font-medium">
                <Crown className="h-[18px] w-[18px]" />
                Plan Premium activo
              </div>
            )}
            <div className="pt-2 border-t mt-1">
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 h-11 touch-manipulation text-muted-foreground text-sm"
                onClick={handleSignOut}
              >
                <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
                Cerrar sesión
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop sidebar — dark navy */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-60 lg:flex-col">
        <div className="flex flex-col flex-1 bg-sidebar border-r border-sidebar-border">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center justify-center px-4 py-5 border-b border-sidebar-border">
            <div className="bg-white rounded-xl p-3 w-full max-w-[200px]">
              <img src={focoLogo} alt="FOCO AI System" className="w-full h-auto" />
            </div>
          </Link>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-0.5">
            {allNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 text-[13px] font-medium',
                    isActive 
                      ? 'bg-sidebar-accent text-sidebar-primary-foreground' 
                      : 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="flex-1">{item.name}</span>
                  {!isPremium && !isAdmin && item.premium && (
                    <Lock className="h-3 w-3 opacity-50" />
                  )}
                </Link>
              );
            })}
            {!isPremium && !isAdmin && (
              <Link
                to="/upgrade"
                className="flex items-center gap-3 px-3 py-2 mt-2 rounded-md bg-sidebar-primary/20 text-sidebar-primary-foreground text-[13px] font-medium"
              >
                <Sparkles className="h-4 w-4" />
                Actualizar a Premium
              </Link>
            )}
            {isPremium && !isAdmin && (
              <div className="flex items-center gap-3 px-3 py-2 mt-2 rounded-md bg-yellow-400/10 text-yellow-400 text-[13px] font-medium">
                <Crown className="h-4 w-4" />
                Plan Premium activo
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="px-3 py-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3 px-3 py-2 mb-1">
              <div className="h-8 w-8 rounded-full bg-sidebar-accent flex items-center justify-center overflow-hidden">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.name || 'Avatar'} className="h-full w-full object-cover" />
                ) : (
                  <User className="h-4 w-4 text-sidebar-foreground/60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-sidebar-foreground truncate">{profile?.name || user?.email}</p>
                <p className={cn(
                  'text-[11px] flex items-center gap-1',
                  isAdmin ? 'text-sidebar-foreground/40' : isPremium ? 'text-yellow-400' : 'text-sidebar-foreground/40'
                )}>
                  {isPremium && !isAdmin && <Crown className="h-3 w-3" />}
                  {isAdmin ? 'Admin' : isPremium ? 'Premium' : 'Gratuito'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 h-8 text-[13px]"
              onClick={() => setGuideOpen(true)}
            >
              <HelpCircle className="h-4 w-4" />
              Guía de uso
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 h-8 text-[13px]"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-60 pt-14 lg:pt-0 min-h-[100dvh] flex flex-col">
        <div className="p-4 sm:p-5 lg:p-8 pb-6 safe-area-inset flex-1">
          {children}
        </div>
        <div className="lg:pl-0">
          <AppFooter />
        </div>
      </main>

      {(isPremium || isAdmin) && <FinancialAdvisorChat />}

      <UserGuideTour open={guideOpen} onOpenChange={setGuideOpen} />
      <OnboardingFlow />
    </div>
    </OnboardingReplayProvider>
  );
}
